import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Scale, Loader2, AlertTriangle, CreditCard } from "lucide-react";

const Linea = ({ etiqueta, antes, despues, testid }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-sm border-b border-border/60 last:border-0">
    <span className="text-muted-foreground">{etiqueta}</span>
    <span className="font-mono text-xs" data-testid={testid}>
      {despues === undefined ? (antes || "—") : (
        <>
          <s className="text-muted-foreground">{antes || "—"}</s>
          <span className="mx-1.5 text-muted-foreground">→</span>
          <b className="text-primary">{despues}</b>
        </>
      )}
    </span>
  </div>
);

/** Cuadra la ficha con los cobros ya registrados. No cobra, no factura, no reenvía recibos. */
export const ReconciliarCobertura = ({ alumnoId, onCambio }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [desvincular, setDesvincular] = useState(false);

  const pedir = async (aplicar) => {
    const marcar = aplicar ? setAplicando : setCargando;
    marcar(true);
    try {
      const { data } = await api.post(`/alumnos/${alumnoId}/reconciliar-cobertura`,
        { aplicar, desvincular_suscripcion: aplicar && desvincular });
      setDatos(data);
      if (aplicar) {
        toast.success("Ficha cuadrada con los cobros registrados");
        onCambio?.();
      } else if (!Object.keys(data.cambios).length) {
        toast.success("La ficha ya cuadra con los cobros registrados");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      marcar(false);
    }
  };

  const confirmar = () => {
    if (!window.confirm(
      "Se corregirá el estado y el periodo de la ficha a partir de los cobros que ya están "
      + "registrados.\n\nNo se cobra nada, no se emite ni duplica ninguna factura y no se "
      + "reenvía el recibo. ¿Continuamos?")) return;
    pedir(true);
  };

  const cambios = datos?.cambios || {};
  const hayCambios = Object.keys(cambios).length > 0 || (datos?.a_borrar || []).length > 0;
  const sus = datos?.suscripcion;
  const cobro = datos?.ledger?.cobro;

  return (
    <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-reconciliar">
      <div className="flex items-center gap-2 mb-1">
        <Scale className="h-5 w-5 text-primary" />
        <h3 className="font-display text-lg font-bold">Cuadrar cobros y ficha</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Compara lo que está cobrado con lo que dice la ficha. Útil cuando un recibo rechazado de
        una domiciliación antigua ha pisado un pago en efectivo o por transferencia.
      </p>

      {!datos ? (
        <Button variant="outline" className="rounded-full font-semibold" onClick={() => pedir(false)}
          disabled={cargando} data-testid="reconciliar-revisar-btn">
          {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Revisar (solo consulta)"}
        </Button>
      ) : (
        <div className="space-y-5">
          <div className="rounded-xl border border-border p-4" data-testid="reconciliar-ledger">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Lo que está cobrado
            </p>
            {cobro ? (
              <>
                <p className="text-sm">
                  <b>{cobro.importe.toFixed(2)} €</b>
                  {cobro.metodo && <> · {cobro.metodo}</>} · pagado el {cobro.fecha_pago}
                  {cobro.factura && <> · factura <b>{cobro.factura}</b></>}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Cubre del {cobro.cubre_desde} al{" "}
                  <b className="text-foreground" data-testid="reconciliar-cubierto-hasta">
                    {datos.ledger.cubierto_hasta}
                  </b>
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No hay ningún cobro con periodo que cuadrar.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border p-4" data-testid="reconciliar-cambios">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              {hayCambios ? "Qué cambiaría en la ficha" : "La ficha ya cuadra"}
            </p>
            <Linea etiqueta="Estado de pago" antes={datos.ficha.estado_pago}
              despues={cambios.estado_pago} testid="rec-estado-pago" />
            <Linea etiqueta="Periodo pagado hasta" antes={datos.ficha.inscripcion_hasta}
              despues={cambios.inscripcion_hasta} testid="rec-inscripcion-hasta" />
            {datos.ficha.pago_fallido_en && (
              <Linea etiqueta="Recibo rechazado el" antes={datos.ficha.pago_fallido_en.slice(0, 10)}
                despues={datos.a_borrar?.includes("pago_fallido_en") ? "se quita" : undefined}
                testid="rec-pago-fallido" />
            )}
            <p className="text-xs text-muted-foreground mt-3">
              Un periodo ya cubierto nunca se acorta, y el cobro, su factura y su recibo no se tocan.
            </p>
          </div>

          {sus && sus.id && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
              data-testid="reconciliar-suscripcion">
              <p className="text-xs uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5" /> Domiciliación en Stripe (solo informativo)
              </p>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="outline" className="rounded-full">{sus.estado}</Badge>
                {sus.activa && (
                  <Badge variant="outline" className="rounded-full border-amber-500/50 text-amber-700 dark:text-amber-400">
                    Sigue activa
                  </Badge>
                )}
                {sus.se_cancela_al_final && (
                  <Badge variant="outline" className="rounded-full">Se cancela al final del periodo</Badge>
                )}
              </div>
              {sus.aviso && <p className="text-xs text-muted-foreground">{sus.aviso}</p>}
              {sus.proximo_cargo && (
                <p className="text-sm" data-testid="reconciliar-proximo-cargo">
                  Próximo cargo previsto: <b>{sus.proximo_cargo}</b>
                  {sus.proximo_importe ? ` por ${sus.proximo_importe.toFixed(2)} €` : ""}
                </p>
              )}
              {sus.activa && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-2 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  Si sigue activa y llega a cobrar, la familia pagaría dos veces. Desde aquí no se
                  cancela nada en Stripe: decídelo tú y hazlo en su panel.
                </p>
              )}
              <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                <Checkbox checked={desvincular} onCheckedChange={(v) => setDesvincular(!!v)}
                  data-testid="reconciliar-desvincular-check" />
                <span className="text-xs text-muted-foreground leading-snug">
                  Desvincular esta domiciliación de la ficha para que deje de mandar sobre su estado
                  de pago. <b>No la cancela en Stripe</b>; queda anotada por si hay que rastrearla.
                </span>
              </label>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full font-semibold" onClick={() => pedir(false)}
              disabled={cargando || aplicando} data-testid="reconciliar-revisar-btn">
              {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Volver a revisar"}
            </Button>
            <Button className="rounded-full font-semibold" onClick={confirmar}
              disabled={aplicando || cargando || !(hayCambios || desvincular)}
              data-testid="reconciliar-aplicar-btn">
              {aplicando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar corrección"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
